/**
 * GeneratingModal - Overlay showing AI is creating the itinerary
 * 
 * Shows rotating messages while trip generates in background.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './GeneratingModal.css';

interface GeneratingModalProps {
    isOpen: boolean;
    destination?: string;
}

const MESSAGES_RO = [
    'Căut cele mai bune atracții...',
    'Optimizez traseul pentru tine...',
    'Adaug recomandări locale...',
    'Verific orele de deschidere...',
    'Creez un program echilibrat...',
    'Finalizez itinerariul...',
];

const MESSAGES_EN = [
    'Finding the best attractions...',
    'Optimizing your route...',
    'Adding local recommendations...',
    'Checking opening hours...',
    'Creating a balanced schedule...',
    'Finalizing your itinerary...',
];

export function GeneratingModal({ isOpen, destination }: GeneratingModalProps) {
    const { i18n } = useTranslation();
    const [messageIndex, setMessageIndex] = useState(0);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);

    const messages = i18n.language.startsWith('ro') ? MESSAGES_RO : MESSAGES_EN;

    // Reset elapsed time when modal opens
    useEffect(() => {
        if (isOpen) {
            setElapsedSeconds(0);
            setMessageIndex(0);
        }
    }, [isOpen]);

    // Track elapsed time
    useEffect(() => {
        if (!isOpen) return;

        const timer = setInterval(() => {
            setElapsedSeconds(prev => prev + 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [isOpen]);

    // Rotate messages every 2.5 seconds
    useEffect(() => {
        if (!isOpen) return;

        const interval = setInterval(() => {
            setMessageIndex(prev => (prev + 1) % messages.length);
        }, 2500);

        return () => clearInterval(interval);
    }, [isOpen, messages.length]);

    if (!isOpen) return null;

    const timeMessage = elapsedSeconds < 10
        ? (i18n.language.startsWith('ro') ? 'De obicei durează ~10 secunde' : 'Usually takes ~10 seconds')
        : (i18n.language.startsWith('ro') ? 'Durează puțin mai mult decât de obicei...' : 'Taking a bit longer than expected...');

    return (
        <div className="generating-modal-overlay">
            <div className="generating-modal">
                {/* Raisa Avatar */}
                <div className="generating-modal-avatar">
                    <img src="/raisa.png" alt="Raisa" />
                </div>

                {/* Raisa. with orange dot */}
                <h2 className="generating-modal-brand">
                    Raisa<span className="generating-modal-dot">.</span>
                </h2>

                {destination && (
                    <p className="generating-modal-destination">{destination}</p>
                )}

                <p className="generating-modal-message">
                    {messages[messageIndex]}
                </p>

                <p className="generating-modal-time">
                    {timeMessage}
                </p>

                <div className="generating-modal-dots">
                    {messages.map((_, i) => (
                        <span
                            key={i}
                            className={`dot ${i === messageIndex ? 'active' : ''}`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

